import { Tooltip, Typography } from '@mui/material';
import {
    DateTimePicker,
    Option,
    Select,
    TextArea,
} from 'components/ui/FormControls';
import React, { ChangeEvent } from 'react';

import {
    ArticleChangeHookType,
    ArticleEditError,
} from '../../../shared/global-enums';
import { ArticleValidationError } from '../../../utils/validator.utils';
import { prepareStaticDataList } from '../../../utils/list-utils';
import { Channel, ChannelTypes, User } from '../../../shared/types';

import { Box } from '@mui/material';
import { Checkbox } from 'components/ui/FormControls';

export interface ArticleDetailsProps {
    selectedContributors: string[];
    detailSummary: string;
    publishDate?: Date;
    listUsers: User[];
    canPublish: boolean;
    errorList: ArticleValidationError[];
    openToAll: boolean;
    showWatermark: boolean;
    displayInChartSection: boolean;
    isPinned: boolean;
    isTimeless: boolean;
    isUserGuideArticle: boolean;
    userGuideScopes: string[];
    availableUserGuideScopes: Option[];
    userHasTableuAccess: boolean;
    selectedChannel?: Channel;
    preparedChannelsList: Channel[];
    selectedOffset: string;
    onChangeValue: (
        changeType: ArticleChangeHookType,
        value: any | any[]
    ) => void;
}

const sortedOffsets: Option[] = [
    { label: '3 days', value: '3d' },
    { label: '1 week', value: '1w' },
    { label: '1 month', value: '1m' },
];

const TIMELESS_TOOLTIP_TEXT =
    'Timeless articles must only contain information that is not date/time sensitive regardless of their publish date';

export const ArticleDetails = (props: ArticleDetailsProps) => {
    const charsLimit = 250;
    const charsLeft = +(
        charsLimit - (props.detailSummary?.length ?? 0)
    ).toFixed();
    const charsMessage = `${charsLeft} characters remaining`;

    const contributorsValue: Option[] = prepareStaticDataList(
        (props.listUsers || []).filter((userOption: User) => {
            return props.selectedContributors?.includes(
                userOption._id.toString()
            );
        })
    );

    const listUsers = prepareStaticDataList(props.listUsers);

    const listChannels = prepareStaticDataList(props.preparedChannelsList);
    const channelValue: Option | undefined = (listChannels || []).find(
        (x) => x.value === props.selectedChannel?._id
    );
    const sortedOffsetsValue: Option | undefined = sortedOffsets.find(
        (_) => _.value === props.selectedOffset
    );
    const userGuideScopesValue: Option[] =
        props.availableUserGuideScopes.filter((option) =>
            props.userGuideScopes.includes(option.value.toString())
        );

    const showOpenToAllCheckbox =
        props.selectedChannel &&
        props.selectedChannel.type !== ChannelTypes.freeOpen;
    const isPremiumChannel =
        props.selectedChannel &&
        props.selectedChannel.type === ChannelTypes.premium;

    const contributorError = props.errorList.find(
        (x) => x.errorType === ArticleEditError.ContributorAbsent
    )?.message;
    const channelError = props.errorList.find(
        (x) => x.errorType === ArticleEditError.ChannelAbsent
    )?.message;
    const summaryError = props.errorList.find(
        (x) => x.errorType === ArticleEditError.SummaryAbsent
    )?.message;
    const showChannelOptions = !props.isUserGuideArticle;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box>
                <Checkbox
                    id="user-guide-article-checkbox"
                    label="Mark as User guide"
                    value="isUserGuideArticle"
                    checked={props.isUserGuideArticle}
                    onChange={({ target }) => {
                        props.onChangeValue(
                            ArticleChangeHookType.DetailsUserGuideArticle,
                            target.checked
                        );
                    }}
                />
                <Box
                    sx={{
                        mt: 1,
                        pr: 6,
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        User guide articles can be targeted to one or more
                        application scopes.
                    </Typography>
                </Box>
            </Box>
            {props.isUserGuideArticle && (
                <Box
                    sx={{
                        mt: 2,
                    }}
                >
                    <Select
                        id="user-guide-scopes-input"
                        label="User guide scopes"
                        size="medium"
                        clearable={true}
                        menuPlacement="bottom"
                        placeholder="Select one or more scopes..."
                        multi={true}
                        value={userGuideScopesValue}
                        onChange={(options: Option[]) => {
                            props.onChangeValue(
                                ArticleChangeHookType.DetailsUserGuideScopes,
                                (options ?? []).map((x) => x.value.toString())
                            );
                        }}
                        options={props.availableUserGuideScopes}
                    />
                    <Box
                        sx={{
                            mt: 1,
                            pr: 6,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            Select one or more scopes. Leave empty to show the
                            user guide everywhere.
                        </Typography>
                    </Box>
                </Box>
            )}
            {showChannelOptions && (
                <>
                    <Box
                        sx={{
                            mt: 2,
                        }}
                    >
                        <Select
                            id="channel-select"
                            label="Channel"
                            size="medium"
                            clearable
                            menuPlacement="bottom"
                            placeholder="Select from the list..."
                            error={!!channelError}
                            errorText={channelError}
                            value={channelValue}
                            onChange={(opt: Option) => {
                                props.onChangeValue(
                                    ArticleChangeHookType.DetailsChannel,
                                    opt?.value?.toString()
                                );
                            }}
                            options={listChannels}
                        />
                    </Box>
                    {showOpenToAllCheckbox && (
                        <>
                            <Box
                                sx={{
                                    mt: 2,
                                }}
                            >
                                <Checkbox
                                    id="premium-checkbox"
                                    label={
                                        isPremiumChannel
                                            ? 'Free for all Insights users'
                                            : 'Open to all Insights users'
                                    }
                                    value="openToAll"
                                    checked={props.openToAll}
                                    onChange={({ target }) => {
                                        props.onChangeValue(
                                            ArticleChangeHookType.DetailsOpenToAll,
                                            target.checked
                                        );
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    mt: 1,
                                    pr: 6,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                    }}
                                >
                                    {isPremiumChannel
                                        ? 'Make the article free for everyone on Insights, even non-subscribers.'
                                        : 'Make the article open and available to all users within Insights.'}
                                </Typography>
                            </Box>
                            {!props.openToAll && (
                                <Box
                                    sx={{
                                        mt: 2,
                                    }}
                                >
                                    <Select
                                        id="premium-offset-select-contributor"
                                        label="Expiry"
                                        size="medium"
                                        clearable
                                        required={false}
                                        menuPlacement="bottom"
                                        placeholder="Select from the list..."
                                        value={sortedOffsetsValue}
                                        onChange={(opt: Option) => {
                                            props.onChangeValue(
                                                ArticleChangeHookType.DetailsPremiumOffset,
                                                opt?.value?.toString()
                                            );
                                        }}
                                        options={sortedOffsets}
                                    />
                                    <Box
                                        sx={{
                                            mt: 1,
                                            pr: 6,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'text.secondary',
                                            }}
                                        >
                                            Starting from the selected day, the
                                            article will become free for
                                            everyone.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </>
            )}
            <Box
                sx={{
                    mt: 2,
                }}
            >
                <Select
                    id="article-select-contributor"
                    size="medium"
                    clearable={true}
                    menuPlacement="bottom"
                    multi={true}
                    label="Contributors"
                    placeholder="Select from the list..."
                    value={contributorsValue}
                    error={!!contributorError}
                    errorText={contributorError}
                    onChange={(options: Option[]) => {
                        props.onChangeValue(
                            ArticleChangeHookType.DetailsContributor,
                            (options ?? []).map((x) => x.value) as string[]
                        );
                    }}
                    options={listUsers}
                />
            </Box>
            <Box
                sx={{
                    mt: 2,
                }}
            >
                <TextArea
                    id="article-input-detail-summary"
                    fullWidth
                    type="text"
                    label="Summary"
                    sx={{
                        '&.MuiInputBase-multiline > .MuiInputBase-input': {
                            fontSize: '1rem',
                            lineHeight: 1.5,
                        },
                    }}
                    value={props.detailSummary}
                    rowsMax={3}
                    error={!!summaryError}
                    errorText={summaryError}
                    showProgressBar={true}
                    progressBarIndicator={props.detailSummary?.length ?? 0}
                    progressBarLimit={charsLimit}
                    progressBarHelperText={charsMessage}
                    onChange={(e: ChangeEvent<any>) => {
                        const val: string = e?.target?.value.slice(0, 250);
                        props.onChangeValue(
                            ArticleChangeHookType.DetailsSummary,
                            val
                        );
                    }}
                />
                <Box
                    sx={{
                        mt: 1,
                    }}
                >
                    <Typography variant="caption">
                        A short extract of the article will be shown in listing
                        pages and the article page.
                    </Typography>
                </Box>
            </Box>
            {props.canPublish && (
                <Box
                    sx={{
                        mt: 2,
                    }}
                >
                    <Box
                        sx={{
                            mt: 1,
                        }}
                    >
                        <Typography
                            component="label"
                            htmlFor="article-date-time-picker"
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                display: 'block',
                                mb: 0.5,
                            }}
                        >
                            Custom Publish date
                        </Typography>
                        <DateTimePicker
                            id="article-date-time-picker"
                            disableFuture={true}
                            hiddenLabel
                            inputProps={{ 'aria-label': 'Custom Publish date' }}
                            onChange={(date: Date | null) => {
                                props.onChangeValue(
                                    ArticleChangeHookType.DetailsPublishDate,
                                    date
                                );
                            }}
                            value={props.publishDate ? props.publishDate : null}
                        />
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    mt: 2,
                }}
            >
                <Checkbox
                    id="watermark-checkbox"
                    label="Show watermark"
                    value="showWatermark"
                    checked={props.showWatermark}
                    onChange={({ target }) => {
                        props.onChangeValue(
                            ArticleChangeHookType.DetailsShowWatermark,
                            target.checked
                        );
                    }}
                />
            </Box>
            <Box
                sx={{
                    mt: 2,
                }}
            >
                <Checkbox
                    id="isPinned-checkbox"
                    label="Pin article"
                    value="isPinned"
                    checked={props.isPinned}
                    onChange={({ target }) => {
                        props.onChangeValue(
                            ArticleChangeHookType.DetailsIsPinned,
                            target.checked
                        );
                    }}
                />
            </Box>
            {props.userHasTableuAccess ? (
                <Box
                    sx={{
                        mt: 2,
                    }}
                >
                    <Checkbox
                        id="display-in-chart-section-checkbox"
                        label="Display in chart section"
                        value="displayInChartSection"
                        checked={props.displayInChartSection}
                        onChange={({ target }) => {
                            props.onChangeValue(
                                ArticleChangeHookType.DetailsDisplayInChartSection,
                                target.checked
                            );
                        }}
                    />
                </Box>
            ) : (
                <></>
            )}
            <Box
                sx={{
                    mt: 2,
                }}
            >
                <Tooltip title={TIMELESS_TOOLTIP_TEXT}>
                    <Box>
                        <Checkbox
                            id="isTimeless-checkbox"
                            label="Timeless article"
                            value="isTimeless"
                            checked={props.isTimeless}
                            onChange={({ target }) => {
                                props.onChangeValue(
                                    ArticleChangeHookType.DetailsIsTimeless,
                                    target.checked
                                );
                            }}
                        />
                    </Box>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default ArticleDetails;
